import {
  type DatabaseFieldGrouping,
  DatabaseGroupSortType,
} from '@databases/types/database/view';
import { compareStringOrNumber } from '@databases/utils/database/view/sort';

import {
  type DatabaseGroup,
  EMPTY_VALUE_GROUP_KEY,
} from './database-group/database-group';
import { type GroupsByKey } from './group-entries-by-field';

/**
 * Sorts a set of groups in ascending or descending order.
 * The empty value group (if present) is placed at the end.
 */
export function sortGroupsAscDesc(
  groupsByKey: Record<string, DatabaseGroup>,
  isAscending: boolean
): DatabaseGroup[] {
  const sortedGroups = Object.values(groupsByKey)
    .filter((group) => !group.isEmptyValueGroup)
    .sort((a, b) => compareStringOrNumber(a.sortValue, b.sortValue, isAscending));

  // Add empty value group at the end
  const emptyValueGroup = groupsByKey[EMPTY_VALUE_GROUP_KEY];
  if (emptyValueGroup) {
    sortedGroups.push(emptyValueGroup);
  }

  return sortedGroups;
}

/**
 * Sorts a set of groups according to a user-defined order.
 * The empty value group (if present and not explicitly ordered) is placed at the end.
 */
export function sortGroupsWithManualOrder(
  groupsByKey: Record<string, DatabaseGroup>,
  manualGroupOrder: string[]
): DatabaseGroup[] {
  const sortedGroups = new Set<DatabaseGroup>();

  // Add groups in the defined order
  manualGroupOrder.forEach((key) => {
    if (groupsByKey[key]) {
      sortedGroups.add(groupsByKey[key]);
    }
  });

  // Append any groups not defined in the manual order
  Object.values(groupsByKey)
    .filter((group) => !group.isEmptyValueGroup)
    .forEach((group) => sortedGroups.add(group));

  // Add empty value group at the end (if not already added)
  const emptyValueGroup = groupsByKey[EMPTY_VALUE_GROUP_KEY];
  if (emptyValueGroup) {
    sortedGroups.add(emptyValueGroup);
  }

  return Array.from(sortedGroups);
}

export function sortGroupsWithGroupingSettings(
  groupsByKey: GroupsByKey,
  fieldGrouping: DatabaseFieldGrouping
): DatabaseGroup[] {
  const { sortType, manualGroupOrder } = fieldGrouping;

  switch (sortType) {
    case DatabaseGroupSortType.ASCENDING: {
      return sortGroupsAscDesc(groupsByKey, true);
    }
    case DatabaseGroupSortType.DESCENDING: {
      return sortGroupsAscDesc(groupsByKey, false);
    }
    case DatabaseGroupSortType.MANUAL: {
      return sortGroupsWithManualOrder(groupsByKey, manualGroupOrder ?? []);
    }
  }
}
